import { useEffect, useState } from "react";
import { Banner, Dialog, Field } from "@/ui";
import { AppError } from "@/api/runtime";
import type { DeclineNotice } from "@/api/tools";
import { DeclineDialog } from "@/views/panel/DeclineDialog";
import { panelDecline } from "@/api/tools";
import { useWrite } from "@/api/useTool";

// F2 / AF-8 + AF-9, V1-INTERACTIONS §4.10.4: the action is labelled "Reschedule / Replace" (the email), the dialog
// asks the call's one question, and the notice rule is stated verbatim. Inside the window the acknowledgement is
// required before Decline is enabled — and the SERVER refuses a decline without it (`panel.decline_round`,
// `notified_manager`), so the rule is not browser-side only. Whether the interviewer is asked to arrange the
// replacement follows the same window: outside it they are not; inside it the rule is exactly that request.

/** Printed verbatim (EM-9 / CALL-17 15:00) with the hours `rules.decline_notice_hours` publishes — 24 as F2 wrote it;
 * the server's refusal quotes the same sentence. */
export const notifyWithin = (hours: number) =>
  `If this request is made within ${hours} hours of the interview, you must notify the Hiring Manager and the Recruiting Coordinator of the replacement interviewer.`;
export const ACKNOWLEDGEMENT = "I have notified my manager on Teams";
/** What the rule asks of the interviewer once the window is reached (CALL-17 14:22 "connect with the hiring manager and
 * find a replacement"; 15:00 "notify the hiring manager and recruiting coordinator who will replace them"), ending in
 * the FAQ's own answer to "What if the interview is less than 24 hours away?", with the published hours. */
export const replaceWithin = (hours: number) =>
  `Because this interview starts within ${hours} hours, connect with the Hiring Manager to find a replacement, and notify the Hiring Manager and the Recruiting Coordinator of the replacement interviewer yourself, as well as recording it here.`;
/** Outside the window the Recruiter and the Hiring Manager arrange the cover (CALL-17 13:52, "let the hiring manager or
 * the recruiter figure that out"). */
export const NOT_ASKED_TO_REPLACE = "You are not asked to find your own replacement.";

/**
 * `notice` is the server's own reading of the window for the holder's seat (`packet.open` → `decline_notice`: the
 * configured hours and whether a live booking starts inside them on the business clock) — never the browser's clock.
 * A refusal that names `notified_manager` means the window was reached after the packet was read (a booking moved,
 * or time passed), so the acknowledgement is shown from then on as well.
 *
 * S15 (CR-083, PLAN D6): a seat released with an acknowledgment declines through the panel's own `DeclineDialog`
 * (`panel.decline`: the Coordinator is told too and the panel change opens). This dialog keeps the first question and,
 * when the holder cannot take the round and `acknowledgmentId` is known, hands over to it; a legacy release (no
 * acknowledgment) keeps the form below, whose `panel.decline_round` is now the alias of `panel.decline`.
 */
export function RescheduleReplaceDialog({ open, notice, busy, error, onClose, onConfirm, acknowledgmentId, requisitionId, interviewStart, onDeclined, roundLabel }: {
  open: boolean;
  notice: DeclineNotice;
  busy?: boolean | undefined;
  error?: AppError | Error | null | undefined;
  onClose: () => void;
  onConfirm: (input: { reason: string; notified_manager: boolean }) => void;
  acknowledgmentId?: string | null | undefined;
  requisitionId?: string | undefined;
  interviewStart?: string | undefined;
  onDeclined?: (() => void) | undefined;
  roundLabel?: string | undefined;
}) {
  const [answer, setAnswer] = useState<"unanswered" | "no">("unanswered");
  const [reason, setReason] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  useEffect(() => {
    if (open) { setAnswer("unanswered"); setReason(""); setAcknowledged(false); }
  }, [open]);
  const within = notice.within_window || (error instanceof AppError && "notified_manager" in error.fieldErrors);
  const blocked = reason.trim().length < 3 || (within && !acknowledged);
  if (open && answer === "no" && acknowledgmentId && requisitionId) {
    return (
      <PanelDecline
        acknowledgmentId={acknowledgmentId}
        requisitionId={requisitionId}
        roundLabel={roundLabel ?? ""}
        interviewStart={interviewStart}
        notice={notice}
        onClose={onClose}
        onDeclined={onDeclined ?? onClose}
      />
    );
  }
  return (
    <Dialog title="Reschedule / Replace" open={open} onClose={onClose}>
      <p id="reschedule-question"><b>Can you still take this interview?</b></p>
      <p className="rule">{notifyWithin(notice.hours)}</p>
      {within && <div className="callout callout--warn" role="status">This interview starts within {notice.hours} hours.</div>}
      {answer === "unanswered" ? (
        <div className="acts">
          <button type="button" className="btn btn--primary" onClick={onClose}>Yes — I can still take it</button>
          <button type="button" className="btn" onClick={() => setAnswer("no")}>No — I cannot take it</button>
        </div>
      ) : (
        <>
          <p className="muted">The Recruiter and the Hiring Manager are told; the backup may be promoted. A held round cannot be declined.{" "}{within ? replaceWithin(notice.hours) : NOT_ASKED_TO_REPLACE}</p>
          <Field label="Why" required id="reschedule-reason">
            <textarea id="reschedule-reason" rows={3} maxLength={1000} value={reason} onChange={(event) => setReason(event.target.value)} />
          </Field>
          {within && (
            <label className="check">
              <input type="checkbox" required checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)} /> {ACKNOWLEDGEMENT}
            </label>
          )}
          <Banner error={error} />
          <div className="acts">
            <button type="button" className="btn" onClick={() => setAnswer("unanswered")}>Back</button>
            <button type="button" className="btn btn--primary" disabled={blocked || !!busy} aria-disabled={blocked || !!busy} onClick={() => onConfirm({ reason: reason.trim(), notified_manager: acknowledged })}>Decline</button>
          </div>
        </>
      )}
    </Dialog>
  );
}

/** The panel's own decline (`panel.decline`) for a seat released with an acknowledgment, in `DeclineDialog`. */
function PanelDecline({ acknowledgmentId, requisitionId, roundLabel, interviewStart, notice, onClose, onDeclined }: {
  acknowledgmentId: string;
  requisitionId: string;
  roundLabel: string;
  interviewStart?: string | undefined;
  notice: DeclineNotice;
  onClose: () => void;
  onDeclined: () => void;
}) {
  const decline = useWrite((input: { reason: string; notified_manager: boolean }, key: string) =>
    panelDecline({
      requisition_id: requisitionId,
      acknowledgment_id: acknowledgmentId,
      reason: input.reason,
      ...(input.notified_manager ? { notified_manager: true } : {}),
    }, key), [["packets"], ["my-acks"], ["today"], ["panel-change"]]);
  return (
    <DeclineDialog
      open
      roundLabel={roundLabel}
      interviewStart={interviewStart ?? null}
      withinWindow={notice.within_window}
      noticeHours={notice.hours}
      busy={decline.isPending}
      error={decline.error ?? undefined}
      onClose={onClose}
      onConfirm={(input) => { void decline.mutateAsync(input).then(onDeclined).catch(() => undefined); }}
    />
  );
}
